import { useEffect, useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useRealTimeActivity } from '@/hooks/useRealTimeActivity';
import { usePerformanceMonitoring } from '@/hooks/usePerformanceMonitoring';
import { useAutomationStatus } from '@/hooks/useAutomationStatus';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, CheckCircle, X, RefreshCw, Bell } from 'lucide-react';
import { MonitoringFallbackUI } from './MonitoringFallbackUI';

interface ProcessingAlert {
  id: string;
  type: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  title: string;
  message: string;
  timestamp: Date;
  acknowledged: boolean;
  actionRequired?: boolean;
}

export function ProcessingAlertSystem() {
  const { toast } = useToast();
  const { stats } = useRealTimeActivity();
  const { alerts, markAlertResolved, connectionStatus, toggleMonitoring } = usePerformanceMonitoring();
  const { gmailAutomation, updateStatus } = useAutomationStatus();
  const [localAlerts, setLocalAlerts] = useState<ProcessingAlert[]>([]);
  const [acknowledged, setAcknowledged] = useState<Set<string>>(new Set());

  // Monitor for alert conditions - simplified to prevent infinite loops
  useEffect(() => {
    const newAlerts: ProcessingAlert[] = [];

    // Connection failure alert
    if (gmailAutomation?.status === 'error') {
      newAlerts.push({
        id: 'connection-error',
        type: 'connection',
        severity: 'critical',
        title: 'Gmail Connection Failed',
        message: gmailAutomation.last_error || 'Unable to connect to Gmail service',
        timestamp: new Date(),
        acknowledged: false,
        actionRequired: true
      });
    }

    // High error rate alert
    if (stats.recentErrors > 5) {
      newAlerts.push({
        id: 'high-error-rate',
        type: 'processing',
        severity: stats.recentErrors > 10 ? 'critical' : 'high',
        title: 'High Error Rate Detected',
        message: `${stats.recentErrors} processing errors in the last 24 hours`,
        timestamp: new Date(),
        acknowledged: false,
        actionRequired: true
      });
    }

    // Low success rate alert
    if (stats.successRate < 70 && stats.totalEvents > 10) {
      newAlerts.push({
        id: 'low-success-rate',
        type: 'performance',
        severity: stats.successRate < 50 ? 'critical' : 'high',
        title: 'Low Success Rate',
        message: `Processing success rate is ${stats.successRate.toFixed(1)}%`,
        timestamp: new Date(),
        acknowledged: false,
        actionRequired: true
      });
    }

    // Only update if there are new alerts and they don't already exist
    if (newAlerts.length > 0) {
      setLocalAlerts(prev => {
        const existingIds = new Set(prev.map(a => a.id));
        const filtered = newAlerts.filter(alert => !existingIds.has(alert.id));
        if (filtered.length > 0) {
          return [...prev, ...filtered];
        }
        return prev;
      });

      // Show toast notifications for new critical alerts
      newAlerts.forEach(alert => {
        if (alert.severity === 'critical') {
          toast({
            title: alert.title,
            description: alert.message,
            variant: "destructive",
          });
        }
      });
    }
  }, [stats.recentErrors, stats.successRate, stats.totalEvents, gmailAutomation?.status, gmailAutomation?.last_error, toast]);

  // Listen for real-time activity events
  useEffect(() => {
    const handleNewActivity = (event: CustomEvent) => {
      const activity = event.detail;
      
      if (activity.status === 'error') {
        const alert: ProcessingAlert = {
          id: `activity-error-${Date.now()}`,
          type: 'processing',
          severity: 'medium',
          title: 'Processing Error',
          message: `Failed to process ${activity.source_type} event`,
          timestamp: new Date(),
          acknowledged: false
        };
        
        setLocalAlerts(prev => [alert, ...prev]);
        
        toast({
          title: "Processing Error",
          description: alert.message,
          variant: "destructive",
        });
      }
    };

    window.addEventListener('newActivity', handleNewActivity as EventListener);
    return () => window.removeEventListener('newActivity', handleNewActivity as EventListener);
  }, [toast]);

  const acknowledgeAlert = (alertId: string) => {
    setAcknowledged(prev => new Set([...prev, alertId]));
    setLocalAlerts(prev => prev.map(alert => 
      alert.id === alertId ? { ...alert, acknowledged: true } : alert
    ));
  };

  const dismissAlert = (alertId: string) => {
    setLocalAlerts(prev => prev.filter(alert => alert.id !== alertId));
    setAcknowledged(prev => {
      const newSet = new Set(prev);
      newSet.delete(alertId);
      return newSet;
    });
  };

  const handleRetryConnection = async () => {
    try {
      await updateStatus({
        automation_type: 'gmail_processing',
        status: 'active',
        last_error: null
      });
      
      dismissAlert('connection-error');
      
      toast({
        title: "Connection Retry",
        description: "Attempting to reconnect to Gmail service...",
      });
    } catch (error) {
      toast({
        title: "Retry Failed",
        description: "Failed to restart the connection. Please check your settings.",
        variant: "destructive",
      });
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'destructive';
      case 'high': return 'destructive';
      case 'medium': return 'secondary';
      case 'low': return 'outline';
      default: return 'outline';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
      case 'high':
        return <AlertTriangle className="h-4 w-4" />;
      case 'medium':
        return <Bell className="h-4 w-4" />;
      default:
        return <CheckCircle className="h-4 w-4" />;
    }
  };

  const activeAlerts = localAlerts.filter(alert => !alert.acknowledged);
  const acknowledgedAlerts = localAlerts.filter(alert => alert.acknowledged);

  // Return null if no alerts to show and monitoring is working
  if (activeAlerts.length === 0 && acknowledgedAlerts.length === 0 && connectionStatus === 'connected') {
    return null;
  }

  return (
    <>
      <MonitoringFallbackUI 
        connectionStatus={connectionStatus}
        onRetry={toggleMonitoring}
        title="Processing Alert System"
        description="Alert monitoring and notification system status"
      />
      
      {(activeAlerts.length > 0 || acknowledgedAlerts.length > 0) && (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5" />
          System Alerts
          {activeAlerts.length > 0 && (
            <Badge variant="destructive" className="ml-auto">
              {activeAlerts.length}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {activeAlerts.map(alert => (
          <Alert key={alert.id} className="relative">
            <div className="flex items-start gap-3">
              {getSeverityIcon(alert.severity)}
              <div className="flex-1 space-y-1">
                <AlertTitle className="flex items-center gap-2">
                  {alert.title}
                  <Badge variant={getSeverityColor(alert.severity) as any}>
                    {alert.severity}
                  </Badge>
                </AlertTitle>
                <AlertDescription>{alert.message}</AlertDescription>
                <div className="text-xs text-muted-foreground">
                  {alert.timestamp.toLocaleString()}
                </div>
                <div className="flex gap-2 mt-2">
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => acknowledgeAlert(alert.id)}
                  >
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Acknowledge
                  </Button>
                  {alert.id === 'connection-error' && (
                    <Button 
                      size="sm" 
                      variant="default"
                      onClick={handleRetryConnection}
                    >
                      <RefreshCw className="h-3 w-3 mr-1" />
                      Retry
                    </Button>
                  )}
                </div>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => dismissAlert(alert.id)}
                className="absolute top-2 right-2"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </Alert>
        ))}

        {acknowledgedAlerts.length > 0 && (
          <div className="pt-4 border-t">
            <h4 className="text-sm font-medium text-muted-foreground mb-2">
              Acknowledged Alerts ({acknowledgedAlerts.length})
            </h4>
            <div className="space-y-2">
              {acknowledgedAlerts.slice(0, 3).map(alert => (
                <div key={alert.id} className="flex items-center justify-between text-sm text-muted-foreground">
                  <span>{alert.title}</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => dismissAlert(alert.id)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
      )}
    </>
  );
}